'use client'

import { ArrowSquareOutIcon } from '@phosphor-icons/react/dist/ssr/ArrowSquareOut'
import { BookmarkIcon } from '@phosphor-icons/react/dist/ssr/Bookmark'
import { PackageIcon } from '@phosphor-icons/react/dist/ssr/Package'
import { TagSimpleIcon } from '@phosphor-icons/react/dist/ssr/TagSimple'
import { TreeViewIcon } from '@phosphor-icons/react/dist/ssr/TreeView'
import { ReviewType } from '@skop-app/lib-domain/types'
import { ReviewStatus } from '@skop-app/lib-types'
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { Button } from '@skop-app/lib-front'

import { openDrawer } from '@/components/drawers/drawer.registry.events'
import { useReview } from '@/context'
import { useGetReview } from '@/services/queries'
import { ReviewParams } from '@/types'
import {
  buildResolvedReviewPopupState,
  buildReviewsByCellKey,
  buildReviewsByElementId,
  findReviewForTargetOrElement,
  getReviewElementSubtitle,
  getReviewElementTitle,
  REVIEW_DRAWER_CLASSNAME,
} from '@/utils/reviews'
import { hasReviewMessages } from '@/utils/reviews'

import { useTranslations } from 'next-intl'
import { Divider } from '..'
import { MessageInput } from '../Messages/Input'
import { MessagesList } from '../Messages/List'
import { ReviewStatusBadge } from '../StatusBadge'

type ReviewProps = {
  reviewPopupParams: ReviewParams
  setReviewPopupParams: Dispatch<SetStateAction<ReviewParams>>
}

export function ReviewPopup({
  reviewPopupParams,
  setReviewPopupParams,
}: ReviewProps) {
  const { position, display, reviewId, target } = reviewPopupParams

  const ref = useRef<HTMLDivElement | null>(null)
  const dragHandleRef = useRef<HTMLDivElement | null>(null)
  const { data: reviewDetail } = useGetReview(reviewId)
  const { resolveReviewPopupState, selectReviewThread } = useReview()
  const t = useTranslations()
  const tNavigation = useTranslations('Navigation')
  const resolvedPopupState = resolveReviewPopupState(target)
  const reviewDetailMatchesTarget =
    reviewDetail &&
    findReviewForTargetOrElement({
      target,
      reviewsByCellKey: buildReviewsByCellKey([reviewDetail], {
        visibleOnly: false,
      }),
      reviewsByElementId: buildReviewsByElementId([reviewDetail], {
        visibleOnly: false,
      }),
    })?.id === reviewDetail.id
  const matchedReview =
    reviewDetail &&
    ((reviewDetailMatchesTarget &&
      (!resolvedPopupState.matchedReview ||
        resolvedPopupState.matchedReview.id === reviewDetail.id)) ||
      (!resolvedPopupState.matchedReview && reviewDetail.id === reviewId))
      ? reviewDetail
      : resolvedPopupState.matchedReview
  const hydratedVisibleReview =
    reviewDetail &&
    reviewDetailMatchesTarget &&
    resolvedPopupState.visibleReview?.id === reviewDetail.id
      ? reviewDetail
      : resolvedPopupState.visibleReview
  const {
    visibleReview,
    composerReview,
    mode,
    drawerReviewId,
    canOpenInDrawer,
  } = buildResolvedReviewPopupState(matchedReview, hydratedVisibleReview)
  const [requestedComposerReviewId, setRequestedComposerReviewId] = useState<
    string | null
  >(null)

  useEffect(() => {
    if (display === 'none') {
      setRequestedComposerReviewId(null)
      return
    }

    if (
      requestedComposerReviewId &&
      requestedComposerReviewId !== visibleReview?.id
    ) {
      setRequestedComposerReviewId(null)
    }
  }, [display, requestedComposerReviewId, visibleReview?.id])

  useReviewPosition({
    reviewElement: ref,
    dragHandleElement: dragHandleRef,
    position,
    setReviewPopupParams,
  })

  // Close popup
  const onCancel = useCallback(() => {
    setReviewPopupParams({
      position: {},
      display: 'none',
      reviewId: undefined,
      target: undefined,
    })
  }, [setReviewPopupParams])

  const onShowInPanel = () => {
    if (!drawerReviewId) return

    openDrawer('reviews', {
      title: tNavigation('reviewNotifications.title'),
      className: REVIEW_DRAWER_CLASSNAME,
    })
    selectReviewThread(drawerReviewId, { revealInDrawer: true })
    onCancel()
  }

  // Close popup when pressing escape
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => event.key === 'Escape' && onCancel(),
    [onCancel],
  )

  useEffect(() => {
    if (display !== 'none') {
      document.addEventListener('keydown', handleKeyDown)
      return () => {
        document.removeEventListener('keydown', handleKeyDown)
      }
    }
  }, [display, handleKeyDown])

  // Close popup when clicking outside
  // COMMENTED OUT: Disabled to allow interaction with grid while popup is open
  // useEffect(() => {
  //   if (display === 'none') return
  //
  //   const handleClickOutside = (event: MouseEvent) => {
  //     if (
  //       ref.current &&
  //       !ref.current.contains(event.target as Node)
  //     ) {
  //       onCancel()
  //     }
  //   }
  //
  //   // Use setTimeout to avoid immediate close when opening
  //   const timeoutId = setTimeout(() => {
  //     document.addEventListener('mousedown', handleClickOutside)
  //   }, 100)
  //
  //   return () => {
  //     clearTimeout(timeoutId)
  //     document.removeEventListener('mousedown', handleClickOutside)
  //   }
  // }, [display, onCancel])

  if (display === 'none') return null

  const title = visibleReview
    ? getReviewElementTitle(visibleReview) || target?.title
    : target?.title
  const subtitle = visibleReview
    ? (getReviewElementSubtitle(visibleReview) ?? target?.subtitle)
    : target?.subtitle
  const type = visibleReview?.type || target?.reviewType || 'default'
  const closedVisibleStatus =
    visibleReview?.status && visibleReview.status !== ReviewStatus.PENDING
      ? visibleReview.status
      : undefined

  const icons = {
    [ReviewType.REVIEW_OPTION]: TreeViewIcon,
    [ReviewType.REVIEW_PRODUCT]: PackageIcon,
    [ReviewType.REVIEW_REFERENCE]: TagSimpleIcon,
    [ReviewType.REVIEW_HEADER]: BookmarkIcon,
    ['default']: PackageIcon,
  }

  // Default to Package icon if type is not found
  const Icon = icons[type] || PackageIcon
  const isClosedVisibleThread = Boolean(
    closedVisibleStatus && visibleReview?.id,
  )
  const isEditing =
    mode === 'create' ||
    (mode === 'thread' && !isClosedVisibleThread) ||
    (visibleReview?.id ? requestedComposerReviewId === visibleReview.id : false)

  return (
    <div
      ref={ref}
      className="absolute z-50 flex w-80 max-w-[calc(100vw-2rem)] flex-col rounded-md border border-neutral-200 bg-neutral-50 p-2 shadow"
      style={{ ...position, display }}
    >
      {title && (
        <>
          <div
            ref={dragHandleRef}
            className="flex cursor-move items-start gap-2 py-1 text-slate-300"
          >
            <Icon className="size-6" />
            <div className="min-w-0 flex-1">
              <div className="line-clamp-1">{title}</div>
              {subtitle ? (
                <div className="line-clamp-1 text-xs text-slate-400">
                  {subtitle}
                </div>
              ) : null}
            </div>
            {canOpenInDrawer ? (
              <button
                onClick={onShowInPanel}
                aria-label={tNavigation('reviewNotifications.title')}
              >
                <ArrowSquareOutIcon className="size-6 hover:text-slate-700" />
              </button>
            ) : null}
          </div>
          <Divider />
        </>
      )}
      <MessagesList
        review={visibleReview}
        onAddComment={
          mode === 'pending-empty' &&
          visibleReview?.status === ReviewStatus.PENDING &&
          !hasReviewMessages(visibleReview)
            ? () =>
                visibleReview?.id &&
                setRequestedComposerReviewId(visibleReview.id)
            : undefined
        }
      />
      {isClosedVisibleThread && !isEditing ? (
        <div className="flex w-full flex-col items-start gap-2 px-0.5 pt-2">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <ReviewStatusBadge status={closedVisibleStatus} />
            <span className="text-xs text-slate-400">
              {t('Review.statusLabel')}
            </span>
          </div>
          <div className="flex w-full justify-end gap-2">
            <Button size="tiny" onClick={onCancel} variant="secondary">
              {t('Common.close')}
            </Button>
            <Button
              size="tiny"
              onClick={() =>
                visibleReview?.id &&
                setRequestedComposerReviewId(visibleReview.id)
              }
            >
              {t('Review.reopen')}
            </Button>
          </div>
        </div>
      ) : null}
      {isEditing && (
        <MessageInput
          review={composerReview}
          target={target}
          onCancel={onCancel}
        />
      )}
    </div>
  )
}

ReviewPopup.displayName = 'ReviewPopup'

// Manage the review position.
const useReviewPosition = ({
  reviewElement,
  dragHandleElement,
  position,
  setReviewPopupParams,
}: {
  reviewElement: React.RefObject<HTMLDivElement | null>
  dragHandleElement: React.RefObject<HTMLDivElement | null>
  position: Record<string, string>
  setReviewPopupParams: Dispatch<SetStateAction<ReviewParams>>
}) => {
  const startPosRef = useRef({ x: 0, y: 0 })
  const startElementPosRef = useRef({ top: 0, left: 0 })
  const isDraggingRef = useRef(false)

  useEffect(() => {
    const el = reviewElement.current
    const handle = dragHandleElement.current
    if (!el || !handle) return

    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null
      if (target?.closest('button, a, input, textarea, select, option')) {
        return
      }

      // Empêcher la sélection de texte pendant le drag
      e.preventDefault()
      isDraggingRef.current = true
      startPosRef.current = { x: e.clientX, y: e.clientY }

      // Sauvegarder la position actuelle de l'élément
      const rect = el.getBoundingClientRect()
      // Parser les valeurs de position (strings comme "100px") en nombres
      const currentTop = position.top
        ? parseFloat(position.top.toString().replace('px', ''))
        : rect.top
      const currentLeft = position.left
        ? parseFloat(position.left.toString().replace('px', ''))
        : rect.left

      startElementPosRef.current = {
        top: currentTop,
        left: currentLeft,
      }
    }

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDraggingRef.current) return

      const dx = e.clientX - startPosRef.current.x
      const dy = e.clientY - startPosRef.current.y

      // Mettre à jour via l'état React pour persister les changements
      setReviewPopupParams((prev) => ({
        ...prev,
        position: {
          ...prev.position,
          top: `${(startElementPosRef.current.top ?? 0) + dy}px`,
          left: `${(startElementPosRef.current.left ?? 0) + dx}px`,
        },
      }))
    }

    const handleMouseUp = () => {
      isDraggingRef.current = false
    }

    handle.addEventListener('mousedown', handleMouseDown)
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      handle.removeEventListener('mousedown', handleMouseDown)
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [dragHandleElement, reviewElement, position, setReviewPopupParams])
}
